import type { FAQ } from "@/components/site/AnswerBlock";
import { STATS_TEXT } from "@/lib/stats";

/**
 * AEO direct-answer copy. Each page gets one short quotable answer near the
 * top plus a matching FAQ set, both mirrored into FAQPage JSON-LD.
 * Questions are written as the phrases agents actually search.
 */

export const COLE_METHOD_ANSWER = {
  question: "What is the COLE Method?",
  answer:
    "The COLE Method is Kristan Cole's four-pillar framework for taking a real estate agent from solo producer to business owner: Clarity (define the business), Operations (install the systems), Leverage (hire and delegate), and Expansion (turn production into ownership). Every agent on the Kristan Cole Network is coached through it.",
};

export const COLE_METHOD_FAQS: FAQ[] = [
  {
    q: "What do the letters in the COLE Method stand for?",
    a: "Clarity, Operations, Leverage, and Expansion. You define the business you actually want, install repeatable systems, add people so you stop being the bottleneck, and then convert production into ownership and long-term wealth.",
  },
  {
    q: "Who is the COLE Method for?",
    a: "Producing agents who have hit a ceiling running the business themselves. It works best for agents doing consistent volume who want leverage, a real operating rhythm, and an ownership path instead of just more transactions.",
  },
  {
    q: "How is the COLE Method different from normal real estate coaching?",
    a: "Most coaching sells accountability. The COLE Method is an operating system paired with a team that already funds the marketing, technology, coaching, and admin infrastructure, so the systems get installed inside a working business rather than handed to you as homework.",
  },
  {
    q: "How long does it take to see results?",
    a: "Clarity and operating rhythm usually shift within the first 90 days. Leverage and expansion are 12 to 36 month arcs, because they depend on hiring, systems maturity, and consistent lead flow.",
  },
];

export const MEET_TEAM_ANSWER = {
  question: "Who leads the Kristan Cole Network?",
  answer:
    `The Kristan Cole Network is led by founder and CEO Kristan Cole, a 40+ year real estate operator, with a leadership team covering operations, coaching, finance, and market growth across ${STATS_TEXT.markets} markets nationwide. In 2025 the network closed more than $1B in volume across 3,000+ transactions.`,
};

export const MEET_TEAM_FAQS: FAQ[] = [
  {
    q: "Who is Kristan Cole?",
    a: "Kristan Cole is the founder and CEO of the Kristan Cole Network, a national network of Keller Williams brokerages. She has more than 40 years in real estate and has been a top-producing agent, brokerage owner, and coach to agents building expansion businesses.",
  },
  {
    q: "Is the Kristan Cole Network part of Keller Williams?",
    a: "Yes. KCN is a network of KW brokerages, so agents get Keller Williams models, training, and profit share alongside KCN's own coaching, marketing, technology, and admin support.",
  },
  {
    q: "How big is the Kristan Cole Network?",
    a: `KCN operates across ${STATS_TEXT.markets} markets nationwide and closed more than $1B in volume across 3,000+ transactions in 2025.`,
  },
  {
    q: "What does the KCN leadership team actually do for agents?",
    a: "Leadership runs operations, coaching, transaction and admin support, marketing, and market-level growth so agents can stay in front of clients instead of running back office infrastructure.",
  },
];

export const LOCATIONS_ANSWER = {
  question: "Where does the Kristan Cole Network operate?",
  answer:
    `The Kristan Cole Network operates in ${STATS_TEXT.markets} markets nationwide, including Alaska, Arizona, California, Florida, Kansas, Michigan, Nebraska, and Wisconsin. Agents can join in an existing market or open a new one with KCN backing.`,
};

export const LOCATIONS_FAQS: FAQ[] = [
  {
    q: "What states is the Kristan Cole Network in?",
    a: `KCN spans ${STATS_TEXT.markets} markets nationwide, with hubs in Alaska and Arizona and additional markets across California, Florida, Kansas, Michigan, Nebraska, and Wisconsin.`,
  },
  {
    q: "Can I join KCN if my market is not listed?",
    a: "Yes. KCN regularly opens new markets with local agents who want to build. If your market is not on the map, the conversation starts with whether you want to join an existing team or launch the market.",
  },
  {
    q: "Do I have to relocate or change my license to join?",
    a: "No relocation. You stay in your market and hang your license with the local KW brokerage in the KCN network, so your existing business and relationships come with you.",
  },
  {
    q: "How do KCN markets work together?",
    a: "Markets share the same COLE Method operating system, coaching, marketing, and technology, plus a referral network across every market, so business moves between markets instead of leaking out of the network.",
  },
];

export const RESULTS_ANSWER = {
  question: "What results does the Kristan Cole Network produce?",
  answer:
    `In 2025 the Kristan Cole Network closed more than $1B in volume across 3,000+ transactions in ${STATS_TEXT.markets} markets nationwide. Agents joining KCN typically grow by adding leverage and systems rather than by working more hours.`,
};

export const RESULTS_FAQS: FAQ[] = [
  {
    q: "How much volume does the Kristan Cole Network close?",
    a: `More than $1B in 2025 volume across 3,000+ transactions, spread across ${STATS_TEXT.markets} markets nationwide.`,
  },
  {
    q: "Do agents actually grow after joining KCN?",
    a: "Yes. The common pattern is a production increase in the first full year driven by leverage, coaching, and consistent lead flow, plus lower out-of-pocket spend because KCN funds marketing, technology, coaching, and admin.",
  },
  {
    q: "What kind of agent does best on this team?",
    a: "Agents with grit, grace, growth, and give. Practically, that means producing agents who want to run a business, take coaching, and build something that outlasts their personal production.",
  },
];

export const TESTIMONIALS_ANSWER = {
  question: "What do agents say about joining the Kristan Cole Network?",
  answer:
    "Agents on the Kristan Cole Network consistently describe three things: more production without more hours, real support for marketing, transactions, and admin, and a clear path from producing agent to business owner. Client reviews cite responsiveness and results.",
};

export const TESTIMONIALS_FAQS: FAQ[] = [
  {
    q: "Are these testimonials from agents or clients?",
    a: "Both. The videos and written stories come from agents who joined the Kristan Cole Network and from clients the team has served.",
  },
  {
    q: "What changes most for agents after joining?",
    a: "Leverage. Marketing, technology, coaching, and transaction and admin support are covered by the team, so agents spend their time on clients and growth instead of running infrastructure.",
  },
  {
    q: "Can I talk to a current KCN agent before joining?",
    a: "Yes. Ask at the Round Table or on a growth strategy session and we will connect you with agents in a similar market and price point.",
  },
];

export const CONTACT_ANSWER = {
  question: "How do I contact the Kristan Cole Network?",
  answer:
    "Call (512) 318-2627, use the form on this page, or visit the Scottsdale office at 15333 N. Pima Road, Suite 130, Scottsdale, AZ 85260. Agents exploring a move usually start with the Monthly Market Round Table before a private conversation.",
};

export const CONTACT_FAQS: FAQ[] = [
  {
    q: "Who should I contact about joining the team?",
    a: "Use this form or call the office and you will be routed to KCN leadership in your market. Every joining conversation starts with a private, no-pressure fit conversation.",
  },
  {
    q: "How fast will someone respond?",
    a: "Typically within one business day. Inquiries from producing agents exploring a move are prioritized.",
  },
  {
    q: "Is the conversation confidential?",
    a: "Yes. Conversations with agents considering a move are confidential and are never shared with your current brokerage.",
  },
];

export const ROUNDTABLE_ANSWER = {
  question: "What is the Monthly Market Round Table?",
  answer:
    "The Round Table is a free, invite-only monthly session with Kristan Cole where producing agents get the current market read and the operating moves behind it. It is the standard first step for agents evaluating the Kristan Cole Network, with no recruiting pitch.",
};

export const ROUNDTABLE_FAQS: FAQ[] = [
  {
    q: "Does the Round Table cost anything?",
    a: "No. It is free and invite-only. We keep the room small so the conversation stays useful for producing agents.",
  },
  {
    q: "Do I have to be interested in joining KCN to attend?",
    a: "No. Most attendees come for the market read and the operating tactics. If joining comes up, it is because you raised it.",
  },
  {
    q: "Who is in the room?",
    a: "Producing agents and team leaders from KCN markets nationwide, plus Kristan Cole and KCN leadership.",
  },
  {
    q: "How do I get an invite?",
    a: "Request one on this page. Invites are confirmed by email with the session link and the agenda.",
  },
];
